import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Feather } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("auth");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const { status, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "authed") navigate("/");
  }, [status, navigate]);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "register" ? { name, email, password, role } : { email, password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      if (data?.requires_verification) {
        setStep("verify");
        setCode("");
        return;
      }
      setUser(data);
      navigate("/");
    } catch (err) {
      if (err.response?.status === 403) {
        setStep("verify");
        setCode("");
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email, code });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    try {
      await api.post("/auth/resend-code", { email });
      toast.success("A fresh code is on its way");
    } catch {
      toast.error("Could not resend right now — try again in a minute");
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setStep("reset");
      setCode("");
      setNewPassword("");
      toast.success("Reset code sent — check your inbox");
    } catch {
      setError("Could not send the reset email — try again in a minute");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { email, code, new_password: newPassword });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="login-page">
      {/* Left editorial panel */}
      <div className="hidden w-1/2 flex-col justify-between bg-foreground p-14 text-background lg:flex">
        <Link to="/" data-testid="login-logo" className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
            <Feather className="h-4 w-4" />
          </span>
          <span className="font-serif text-lg font-semibold tracking-tight">Campus Field Journal</span>
        </Link>
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-6xl font-semibold leading-[0.95] tracking-tighter"
          >
            Every sighting<br />deserves a<br /><span className="italic text-primary">page.</span>
          </motion.h1>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-background/60">
            Sign in to add your own photographs and writings, like the entries that move you, and pin your sightings to the campus map.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.3em] text-background/40">Open to students &amp; teachers</p>
      </div>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <Link to="/" data-testid="login-back-home" className="mb-10 inline-block text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:text-foreground lg:hidden">
            ← Campus Field Journal
          </Link>

          {step === "verify" ? (
            <div data-testid="verify-step">
              <h2 className="font-serif text-4xl font-semibold tracking-tight">Check your inbox.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                We emailed a 6-digit code to <span className="font-bold text-foreground">{email}</span>. Enter it below to open your journal. It expires in 15 minutes.
              </p>
              <form onSubmit={submitVerify} className="mt-8 space-y-5" data-testid="verify-form">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="verify-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="······"
                  className="w-full border border-border bg-card px-4 py-4 text-center font-serif text-3xl tracking-[0.5em] outline-none transition-colors focus:border-primary"
                />
                {error && (
                  <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="verify-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="verify-submit-button"
                  className="w-full bg-primary px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {busy ? "Verifying…" : "Verify & sign in"}
                </button>
              </form>
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={resendCode}
                  data-testid="resend-code-button"
                  className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                >
                  Resend code
                </button>
                <button
                  onClick={() => { setStep("auth"); setError(""); setCode(""); }}
                  data-testid="verify-back-button"
                  className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                >
                  Back
                </button>
              </div>
            </div>
          ) : step === "forgot" ? (
            <div data-testid="forgot-step">
              <h2 className="font-serif text-4xl font-semibold tracking-tight">Forgot your password?</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Enter your account email and we'll send you a 6-digit reset code.
              </p>
              <form onSubmit={submitForgot} className="mt-8 space-y-5" data-testid="forgot-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  required
                  placeholder="you@school.edu"
                  className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                />
                {error && (
                  <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="forgot-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-submit-button"
                  className="w-full bg-primary px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {busy ? "Sending…" : "Email me a reset code"}
                </button>
              </form>
              <button
                onClick={() => { setStep("auth"); setError(""); }}
                data-testid="forgot-back-button"
                className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
              >
                Back to sign in
              </button>
            </div>
          ) : step === "reset" ? (
            <div data-testid="reset-step">
              <h2 className="font-serif text-4xl font-semibold tracking-tight">Set a new password.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                We emailed a 6-digit code to <span className="font-bold text-foreground">{email}</span>. Enter it with your new password. It expires in 15 minutes.
              </p>
              <form onSubmit={submitReset} className="mt-8 space-y-5" data-testid="reset-form">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  data-testid="reset-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  className="w-full border border-border bg-card px-4 py-4 text-center font-serif text-3xl tracking-[0.5em] outline-none transition-colors focus:border-primary"
                />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="reset-password-input"
                  required
                  minLength={6}
                  placeholder="New password (6+ characters)"
                  className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                />
                {error && (
                  <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="reset-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={busy || code.length !== 6 || newPassword.length < 6}
                  data-testid="reset-submit-button"
                  className="w-full bg-primary px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {busy ? "Resetting…" : "Reset & sign in"}
                </button>
              </form>
              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={submitForgot}
                  data-testid="reset-resend-button"
                  className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                >
                  Resend code
                </button>
                <button
                  onClick={() => { setStep("auth"); setError(""); setCode(""); setNewPassword(""); }}
                  data-testid="reset-back-button"
                  className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-foreground"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
          <>
          <div className="flex border border-border" data-testid="auth-mode-tabs">
            {["login", "register"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                data-testid={`auth-tab-${m}`}
                className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300 ${
                  mode === m ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {m === "login" ? "Sign in" : "Join"}
              </button>
            ))}
          </div>

          <h2 className="mt-10 font-serif text-4xl font-semibold tracking-tight" data-testid="auth-form-title">
            {mode === "login" ? "Welcome back to the field." : "Open your journal."}
          </h2>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Full name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="register-name-input"
                    required
                    className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    placeholder="Jordan Rivera"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">I am a</label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    data-testid="register-role-select"
                    className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="auth-email-input"
                required
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                placeholder="you@school.edu"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="auth-password-input"
                required
                minLength={6}
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                placeholder="6+ characters"
              />
            </div>

            {mode === "login" && (
              <button
                type="button"
                onClick={() => { setStep("forgot"); setError(""); }}
                data-testid="forgot-password-link"
                className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-primary"
              >
                Forgot your password?
              </button>
            )}

            {error && (
              <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full bg-primary px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">or</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <button
            onClick={googleSignIn}
            data-testid="google-signin-button"
            className="flex w-full items-center justify-center gap-3 border border-foreground px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" opacity=".7" />
              <path fill="currentColor" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" opacity=".5" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" opacity=".9" />
            </svg>
            Continue with Google
          </button>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
